/**
 * Create Organization
 * Form for creating a new Geschäftsbereich
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import type { OrganizationType } from "@jourfx/types/organizations";
import { SuperAdminGuard } from "../../components/SuperAdminGuard";

interface FormData {
  name: string;
  slug: string;
  type: OrganizationType;
}

function OrganizationCreateContent() {
  const { t } = useTranslation("organizations");
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrganization = useMutation(api.admin.organizations.create);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    slug: "",
    type: "md",
  });

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleNameChange = (name: string) => {
    updateField("name", name);
    const slug = name
      .toLowerCase()
      .replace(/[äöüß]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" })[c] || c)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    updateField("slug", slug);
  };

  const validate = (): boolean => {
    if (!formData.name.trim()) {
      setError(t("create.errors.nameRequired"));
      return false;
    }
    if (!formData.slug.trim()) {
      setError(t("create.errors.slugRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await createOrganization({
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        type: formData.type,
      });
      navigate({ to: "/organizations" });
    } catch (err) {
      console.error("Failed to create organization:", err);
      setError(err instanceof Error ? err.message : t("create.errors.unknown"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/organizations" className="rounded-lg p-2 hover:bg-muted">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("create.title")}</h1>
          <p className="text-muted-foreground">{t("create.subtitle")}</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-lg border bg-card p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t("create.details.title")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-foreground" htmlFor="organization-name">
                {t("create.details.name")} *
              </label>
              <input
                id="organization-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                autoComplete="off"
                className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
                placeholder={t("create.details.namePlaceholder")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground" htmlFor="organization-slug">
                {t("create.details.slug")} *
              </label>
              <input
                id="organization-slug"
                type="text"
                value={formData.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                autoComplete="off"
                className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
                placeholder={t("create.details.slugPlaceholder")}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground" htmlFor="organization-type">
                {t("create.details.type")} *
              </label>
              <select
                id="organization-type"
                value={formData.type}
                onChange={(e) => updateField("type", e.target.value as OrganizationType)}
                autoComplete="off"
                className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
              >
                <option value="md">{t("create.types.md")}</option>
                <option value="clinic">{t("create.types.clinic")}</option>
                <option value="practice">{t("create.types.practice")}</option>
                <option value="insurance">{t("create.types.insurance")}</option>
                <option value="other">{t("create.types.other")}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3 border-t pt-6">
          <Link
            to="/organizations"
            className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            {t("create.cancel")}
          </Link>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 rounded-lg bg-success px-4 py-2 text-sm text-success-foreground hover:bg-success/90 disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("create.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrganizationCreate() {
  return (
    <SuperAdminGuard>
      <OrganizationCreateContent />
    </SuperAdminGuard>
  );
}
